import {
  ISharedFileRequestSettingsJob,
  SharedFileRequestJobType
} from "@wingspanhq/shared-files/dist/lib/interfaces";

//https://github.com/wingspanHQ/users/blob/staging/src/lib/services/identityVerification/identityVerificationService.ts#L336-L344
export const IDENTITY_VERIFICATION_FAILURE_RISK_SIGNALS = [
  "documentExpired",
  "documentNotVerified",
  "documentPossibleImageTampering",
  "documentSelfieNotLiveImage",
  "documentPossibleFakeImage",
  "documentSelfieDoesNotMatch",
  "documentSelfieWasSkipped"
];

// latest docs can be found here: https://linear.app/wingspan/issue/PLA-744/update-validations-copy-for-id-verification-risk-signals
export const IDENTITY_VERIFICATION_CHECKS: (ISharedFileRequestSettingsJob & {
  riskSignals: Record<string, string>;
  successSummary: string;
  errorSummary: string;
  successWithWarningsSummary: string;
})[] = [
  {
    title: "Document Authenticity & Quality",
    description:
      "The contractor must submit a clear image of their original, physical government ID. Photocopies and digital screenshots are not accepted.",
    type: SharedFileRequestJobType.Validation,
    successSummary:
      "The document has passed all authenticity and quality checks.",
    errorSummary: "The document has failed authenticity and quality checks.",
    successWithWarningsSummary:
      "The document has passed authenticity and quality checks, but some warnings were raised.",
    riskSignals: {
      documentNotVerified:
        "Unable to verify document - unable to read or failed tamper/validation checks.",
      documentPossibleImageTampering:
        "Signs of tampering detected: The document may have been physically or digitally altered.",
      documentPossibleImageAlterationFront:
        "Signs of tampering on front: The front of the document shows signs of alteration.",
      documentPossibleImageAlterationBack:
        "Signs of tampering on back: The back of the document shows signs of alteration.",
      documentPossibleFakeImage:
        "The image of the document has evidence or appearances of being a fake document.",
      documentAlignmentFailed:
        "Inconsistent layout: The text or photo alignment is irregular, which can indicate a forgery.",
      documentPdf417DataIsNotValid:
        "Invalid barcode data: The document's barcode is malformed, a common indicator of a non-genuine document.",
      documentPossibleDigitalFraud:
        "Digital manipulation detected: The image file shows evidence of being edited with photo software.",
      documentMrzLineFormatCheck:
        "Invalid MRZ: The Machine-Readable Zone (e.g., the text at the bottom of a passport) has an invalid format.",
      documentQrCodeCheck:
        "Invalid QR code: The QR code could not be validated, suggesting it may not be a genuine document.",
      documentPhotoIsScreenCapture:
        "Photo of a screen submitted: The image was taken of a phone or computer screen, not the physical ID.",
      documentPhotoIsPaperCapture:
        "Photocopy submitted: The image was taken of a paper copy, not the original physical ID.",
      documentCouldNotClassify:
        "Unidentifiable document type: The system could not determine the type of ID from the image.",
      documentBarcodeCouldNotBeRead:
        "Unreadable barcode: The image quality was too poor (blurry, dark, or obstructed) to read the barcode.",
      documentBarcodeCouldNotBeDetected:
        "Barcode not found: A barcode could not be detected on the document image.",
      documentVisiblePhotoFeaturesNotVerified:
        "Security features not verified: The document's visible security features (e.g., holograms) could not be validated."
    }
  },
  {
    title: "Information Consistency",
    description:
      "All information on the document, such as name and date of birth, must be consistent between the printed text and the barcode.",
    type: SharedFileRequestJobType.Validation,
    successSummary:
      "All data extracted from the document is consistent across its different parts.",
    successWithWarningsSummary:
      "The data extracted from the document is consistent across its different parts, but some warnings were raised.",
    errorSummary: "The document has information inconsistency.",
    riskSignals: {
      documentDobCrosscheckDoesNotMatch:
        "Date of birth mismatch: The DOB printed on the front does not match the DOB in the barcode.",
      documentFullNameCrosscheckDoesNotMatch:
        "Name mismatch: The name printed on the front does not match the name in the barcode.",
      documentExpirationDateCrosscheckDoesNotMatch:
        "Expiration date mismatch: The expiration date on the front does not match the one in the barcode.",
      documentNumberCrosscheckDoesNotMatch:
        "Document number mismatch: The ID number on the front does not match the number in the barcode.",
      documentSexCrosscheckDoesNotMatch:
        "Sex mismatch: The sex listed on the front does not match what is listed in the barcode.",
      documentBarcodeContentDoesNotMatch:
        "Barcode content mismatch: The content encoded in the barcode is inconsistent with the information printed on the ID.",
      documentNumberCheckDigitDoesNotMatch:
        "Invalid document number: A technical verification check on the document number failed.",
      documentDobCheckDigitDoesNotMatch:
        "Invalid date of birth: A technical verification check on the date of birth failed.",
      documentExpirationCheckDigitDoesNotMatch:
        "Invalid expiration date: A technical verification check on the expiration date failed."
    }
  },
  {
    title: "Face Match & Liveness",
    description:
      "The contractor must submit a live selfie that clearly matches the photo on their ID.",
    type: SharedFileRequestJobType.Validation,
    successSummary:
      "The selfie matches the ID photo and passed the liveness check.",
    successWithWarningsSummary:
      "The selfie passed the liveness check, but some warnings were raised.",
    errorSummary: "The selfie failed the ID photo match and liveness check.",
    riskSignals: {
      documentSelfieWasSkipped: "The selfie failed to capture successfully.",
      documentSelfieDoesNotMatch:
        "Face does not match ID: The person in the selfie does not appear to be the same person in the ID photo.",
      documentSelfieNotLiveImage:
        "Liveness check failed: The system detected the selfie was not a live photo (e.g., a photo of a screen).",
      documentSelfieUsedWithDifferentInformation:
        "Duplicate selfie detected: This selfie has been used in a verification attempt with a different ID or personal information."
    }
  },
  {
    title: "Document Validity",
    description:
      "The submitted document must be current and not past its expiration date.",
    type: SharedFileRequestJobType.Validation,
    successSummary: "The document is current and not expired.",
    successWithWarningsSummary:
      "The document is current and not expired, but some warnings were raised.",
    errorSummary: "The document is expired.",
    riskSignals: {
      documentExpired:
        "Document is expired: The submitted ID is past its expiration date."
    }
  }
];
